import { Guid } from "guid-typescript";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IRootState } from "../../../types/redux/IRootStore";
import { Label } from "../../../types/label";
import ReactSelectWrapper, { IReactSelectOption } from "../../common/ReactSeletWrapper";
import { ReactSelectStyle } from "../../common/rectSelectStyle";
import { Printer } from "../../../types/printer";
import { useAppDispatch } from "../../../redux/hooks";
import AplSettingsSelector from "../../../redux/helpers/AplSettingsSelector";
import { onSetTestPrintLabelId, onSetTestPrintPrinterId, testPrinters } from "../../../redux/slices/workorderManagement.feature";
import { testPrintPallets } from "../../../aplApi/printHelper";
import { TestPrintParams } from "../../../types/testPrintParams";
import { bootboxAlert } from "../../common/bootboxWrapper";
import { tryCatchBootbox } from "../../../aplApi/apiTryCatch";
import { stringContains } from "../../../aplUtils/stringUtills";
import { getPdfUrl } from "../../../aplApi/pdfHelper";
import DownloadButtonTestPrint from "../../common/Modals/DownloadButtonTestPrint";

export default function WorkorderManagementTestPrintPane() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const aplSettings = AplSettingsSelector();
    const workorderManagementRedux = useSelector((state: IRootState) => state.workorderManagement);

    const [spinnerVisible, setSpinnerVisible] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState("");

    const labelsSelectData: IReactSelectOption[] =
        workorderManagementRedux.labelsData?.labels == null
            ? []
            : workorderManagementRedux.labelsData.labels.map((x: Label) => ({
                  value: x.labelId,
                  label: x.labelName
              }));

    const printersSelectData: IReactSelectOption[] = workorderManagementRedux.testPrintPrinters.map((x: Printer) => ({
        value: x.printerId,
        label: x.printerName
    }));

    const sendPrint = async () => {
        setSpinnerVisible(true);
        setDownloadUrl("");
        if (!workorderManagementRedux.orderData) {
            bootboxAlert("Workorder data not loaded");
            return;
        }
        let testPrintParams: TestPrintParams = {
            lineId: workorderManagementRedux.orderData.lineId,
            workOrderId: workorderManagementRedux.orderData.workorderId,
            printerId: workorderManagementRedux.testPrintSelectedPrinterId,
            copies: 1,
            labelId: workorderManagementRedux.testPrintSelectedLabelId,
            culture: "en-GB",
            printId: Guid.create().toString()
        };

        await tryCatchBootbox(async () => {
            let printHistoryRequest = (await testPrintPallets(testPrintParams)).data;
            if (!printHistoryRequest) {
                bootboxAlert("Returned data was null");
                setDownloadUrl("");
                return;
            }
            if (printHistoryRequest && !printHistoryRequest.success) {
                bootboxAlert(`Error: ${printHistoryRequest.errorMessage}`);
                setDownloadUrl("");
                return;
            }
            if (printHistoryRequest && printHistoryRequest.success && stringContains(printHistoryRequest.printerName, "pdf")) {
                let pdfUrl = getPdfUrl(printHistoryRequest.printId);
                setDownloadUrl(pdfUrl);
                // window.open(pdfUrl, "_blank", "noreferrer");
            }
        });

        setSpinnerVisible(false);
    };

    const handleChangeTestLabel = (e: any) => {
        dispatch(onSetTestPrintLabelId(e.value));
    };

    const handleChangeTestPrinter = (e: any) => {
        dispatch(onSetTestPrintPrinterId(e.value));
    };

    useEffect(() => {
        dispatch(testPrinters(aplSettings.siteId) as any);
    }, [aplSettings.siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (workorderManagementRedux.labelsData && workorderManagementRedux.labelsData.labels.length > 0) {
            let labelId = workorderManagementRedux.labelsData?.labels.find(x => x.labelName.toLowerCase() === workorderManagementRedux.labelsData?.palLabelName.toLowerCase())?.labelId ?? 0;
            dispatch(onSetTestPrintLabelId(labelId));
        }
        return () => {
            dispatch(onSetTestPrintLabelId(0));
        };
    }, [dispatch, workorderManagementRedux.labelsData]);

    useEffect(() => {
        if (workorderManagementRedux.testPrintPrinters.length > 0) {
            dispatch(onSetTestPrintPrinterId(workorderManagementRedux.testPrintPrinters[0].printerId));
        }
        return () => {
            dispatch(onSetTestPrintPrinterId(0));
        };
    }, [aplSettings.siteId, dispatch, workorderManagementRedux.testPrintPrinters]);

    return (
        <React.Fragment>
            <div className="container mw-100 p-2 pt-0 mt-1">
                <div className="row ml-1">
                    <div className="col-sm-3">
                        <label htmlFor="txtReprintLabelTest">{t("main.litPalLabel")}</label>
                    </div>
                    <div className="col-sm-3">
                        <ReactSelectWrapper
                            data={labelsSelectData}
                            isClearable={false}
                            styles={ReactSelectStyle}
                            onChange={handleChangeTestLabel as any}
                            value={labelsSelectData.find((x: any) => x.value === workorderManagementRedux.testPrintSelectedLabelId)}
                        />
                    </div>
                </div>
                <div className="row ml-1">
                    <div className="col-sm-3">
                        <label htmlFor="ddPrinterNameModalTest">{t("main.printerName")}</label>
                    </div>
                    <div className="col-sm-3">
                        <ReactSelectWrapper
                            data={printersSelectData}
                            isClearable={false}
                            styles={ReactSelectStyle}
                            onChange={handleChangeTestPrinter as any}
                            value={printersSelectData.find(x => x.value === workorderManagementRedux.testPrintSelectedPrinterId)}
                        />
                    </div>
                </div>
                <div className="row ml-1">&nbsp;</div>
                <div className="row ml-1">
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3">
                        <span className="float-right">
                            {downloadUrl && <DownloadButtonTestPrint pdfUrl={downloadUrl} />}
                            &nbsp;
                            <button id="btnTestPrint" className="btn btn-primary" type="button" onClick={sendPrint}>
                                <span
                                    id="btnTestPrintSpinner"
                                    className={`spinner-border spinner-border-sm ${!spinnerVisible ? "d-none" : ""}`}
                                    role="status"
                                ></span>
                                {t("main.testPrint")}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
